import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import type { FeedPost } from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import {
  bridgeEventStreamAtom,
  daemonPushFrameStreamAtom,
  sdkConfiguredAtom,
} from '../stores/sdk/atoms';
import { liveFeedHandleAtom, liveFeedUnseenCountAtom } from '../stores/sdk/live-feed-atom';

export function useLiveFeedHandle(): void {
  const ready = useAtomValue(sdkConfiguredAtom);
  const authenticated = useAtomValue(holdsIdentityAtom);
  const webStream = useAtomValue(bridgeEventStreamAtom);
  const daemonStream = useAtomValue(daemonPushFrameStreamAtom);
  const setHandle = useSetAtom(liveFeedHandleAtom);
  const setUnseen = useSetAtom(liveFeedUnseenCountAtom);

  useEffect(() => {
    if (!ready || !authenticated) {
      setHandle(null);
      setUnseen(0);
      return;
    }
    let cancelled = false;
    let offChange: (() => void) | undefined;
    void getSDK()
      .feed.connect()
      .then((feed) => {
        if (cancelled) return;
        offChange = feed.onChange(() => {
          setUnseen(feed.unseenCount);
        });
        setHandle(feed);
        setUnseen(feed.unseenCount);
      });
    return () => {
      cancelled = true;
      offChange?.();
      setHandle(null);
    };
  }, [ready, authenticated, setHandle, setUnseen]);

  const handle = useAtomValue(liveFeedHandleAtom);
  useEffect(() => {
    if (!handle) return;
    const stream = webStream ?? daemonStream;
    if (!stream) return;
    return stream.registerFeed((post: FeedPost, meta) => {
      handle.apply(post, { notice: !meta.replay, trustViewer: false });
    });
  }, [handle, webStream, daemonStream]);
}
